import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { Checkbox, Divider, InputNumber, ModalProps } from 'antd';
import FormField from 'components/FormField';
import SafeImage from 'components/SafeImage';
import StickyModal from 'components/StickyModal';
import { Formik } from 'formik';
import { useMemo } from 'react';

function SubSkusModal({
  currency,
  warehouseName,
  erpItemSubSkus,
  subSkus,
  onSubSkusChange,
  ...otherProps
}: {
  currency?: string;
  warehouseName?: string;
  erpItemSubSkus?: Todo.ErpItemSku[];
  subSkus: Todo.ErpOrderSku[];
  onSubSkusChange: (subSkus: Todo.ErpOrderSku[]) => void;
} & ModalProps) {
  const { t } = useTranslate();

  const disabled = !erpItemSubSkus;

  const initialValues = useMemo(() => {
    if (erpItemSubSkus) {
      return erpItemSubSkus.map((sku) => {
        const target = subSkus.find((_) => _.skuId === sku.skuId);
        return {
          ...sku,
          checked: Boolean(target),
          skuQuantity: target?.skuQuantity ?? 1,
          skuAmount: target?.skuAmount ?? '0',
        };
      });
    }
    return subSkus.map((sku) => {
      return {
        ...sku,
        inventories: [],
        checked: true,
      };
    });
  }, [erpItemSubSkus, subSkus]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(skus) => {
        onSubSkusChange(
          skus
            .filter(({ checked }) => checked)
            .map(({ inventories, checked, ...others }) => {
              return others as any;
            }),
        );
        otherProps.onCancel?.({} as any);
      }}
    >
      {({ values, handleSubmit }) => {
        const list = values.map((_, index) => {
          return (
            <FormField<(typeof values)[0]> noStyle name={`${index}`}>
              {({ field: { value, onChange } }) => {
                const { checked, imgUrl, skuName, skuId, inventories } = value;
                const inventory = inventories.find((_) => _.warehouseName === warehouseName);
                const invalid =
                  inventory && value.skuQuantity && inventory.inventory < value.skuQuantity;
                return (
                  <Item
                    style={
                      invalid
                        ? {
                            borderColor: '#FF4747',
                            background: '#FFF7F7',
                          }
                        : undefined
                    }
                  >
                    {!disabled && (
                      <Checkbox
                        checked={checked}
                        onChange={({ target: { checked } }) => onChange({ ...value, checked })}
                      />
                    )}
                    <SafeImage src={imgUrl} width={60} height={60} />
                    <section>
                      <p>{skuName}</p>
                      <div>
                        {t('商品编码')}：{skuId}
                        {warehouseName && (
                          <>
                            <Divider type="vertical" />
                            {warehouseName}
                          </>
                        )}
                        {inventory && (
                          <>
                            <Divider type="vertical" />
                            {t('库存')}：{inventory.inventory}
                          </>
                        )}
                      </div>
                    </section>
                    <span>
                      <FormField<number>
                        name={`${index}.skuQuantity`}
                        label={t('件数')}
                        validate={(value) =>
                          value || value === 0 || !checked
                            ? undefined
                            : t('department.请输入x', { x: t('件数') })
                        }
                      >
                        {({ field: { value, onChange, onBlur } }) => {
                          return (
                            <InputNumber
                              disabled={disabled}
                              precision={0}
                              min={1}
                              value={value}
                              onChange={onChange as any}
                              onBlur={onBlur}
                            />
                          );
                        }}
                      </FormField>
                      <FormField<string>
                        name={`${index}.skuAmount`}
                        label={t('金额')}
                        validate={(value) =>
                          value || !checked ? undefined : t('department.请输入x', { x: t('金额') })
                        }
                      >
                        {({ field: { value, onChange, onBlur } }) => {
                          return (
                            <InputNumber
                              disabled={disabled}
                              min={0}
                              value={value ? Number(value) : undefined}
                              onChange={(value) =>
                                onChange(typeof value === 'number' ? value.toString() : '')
                              }
                              onBlur={onBlur}
                              addonAfter={currency}
                            />
                          );
                        }}
                      </FormField>
                    </span>
                  </Item>
                );
              }}
            </FormField>
          );
        });

        return (
          <StickyModal
            width={600}
            title={disabled ? `${t('已选择补发子商品')}（${subSkus.length}）` : t('选择补发子商品')}
            okText={t('创建')}
            onOk={() => handleSubmit()}
            {...otherProps}
          >
            <Container>
              {erpItemSubSkus && (
                <div>
                  {t('子商品数量')}({erpItemSubSkus.length})
                  <span>{t('aide.已选x项', { x: values.filter((_) => _.checked).length })}</span>
                </div>
              )}
              <section>{list}</section>
            </Container>
          </StickyModal>
        );
      }}
    </Formik>
  );
}

const Item = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  margin-bottom: 16px;
  background: #f3f5f7;
  border: solid 1px #f3f5f7;
  > label {
    flex: 0 0 auto;
    margin-right: 10px;
  }
  > div {
    flex: 0 0 auto;
    margin-right: 10px;
  }
  > section {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    overflow: hidden;
    > p {
      margin-bottom: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    }
    > div {
      margin-bottom: 0;
      font-size: 12px;
      color: #84868c;
    }
  }
  > span {
    margin-left: 12px;
    .ant-form-item {
      justify-content: flex-end;
      margin-bottom: 0;
      &:first-of-type {
        margin-bottom: 6px;
      }
    }
    .ant-form-item-explain {
      display: none;
    }
    .ant-form-item-control {
      flex: 0 0 auto;
      width: 120px;
    }
    .ant-form-item-control-input-content > * {
      width: 120px;
    }
  }
`;

const Container = styled.div`
  margin: -24px;
  padding: 14px;
  > div {
    margin-bottom: 16px;
    > span {
      display: inline-block;
      margin-left: 12px;
      line-height: 24px;
      padding: 0 8px;
      border-radius: 4px;
      color: var(--ant-primary-color);
      background: var(--ant-primary-2);
    }
  }
`;

export default SubSkusModal;
